import lottie, { AnimationItem } from 'lottie-web';
import React, { useCallback, useEffect, useRef } from 'react';
import { Button, ButtonProps } from './button';
import styles from './lottie.css';

/**
 * Plays Lotte animations.  Put on your old school uniform and pack some Pepero.
 *
 * Oh you said Lottie? I thought you said Lotte.
 **/
export function LottiePlayer({
  animationData,
  loop = true,
  delayMs = 0,
  hideOnComplete = false,
}: {
  animationData: any;
  loop?: boolean;
  delayMs?: number;
  hideOnComplete?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const container = ref.current;
    const anim = lottie.loadAnimation({
      container,
      // our star animation doesn't render at all with the canvas renderer
      // test that before switching to canvas
      renderer: 'svg',
      loop,
      autoplay: false,
      animationData,
    });
    if (hideOnComplete) {
      anim.addEventListener('complete', () => {
        container.style.opacity = '0';
      });
    }
    const timeout = setTimeout(() => {
      container.style.opacity = '1';
      anim.play();
    }, delayMs);
    return () => {
      anim.destroy();
      clearTimeout(timeout);
    };
  }, [animationData, loop, delayMs, hideOnComplete]);
  return (
    <div ref={ref} style={{ width: '100%', height: '100%', opacity: 0 }} />
  );
}

export const LottieButton = ({
  animationData,
  onClick,
  ...passthrough
}: Exclude<ButtonProps, 'icon'> & { animationData: any }) => {
  const ref = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const container = ref.current;
    const anim = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData,
    });
    animRef.current = anim;

    return () => {
      anim.destroy();
      animRef.current = null;
    };
  }, [animationData]);

  const handleClickAndPlay = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (animRef.current) {
        animRef.current.goToAndPlay(0);
      }
      onClick?.(e);
    },
    [onClick],
  );

  return (
    <Button
      {...passthrough}
      onClick={handleClickAndPlay}
      icon={<div className={styles.animatedIcon} ref={ref} />}
    />
  );
};
